import cloneDeep from 'lodash.clonedeep';

import { ChartDataSeries } from '../ChartWidget.types';

/**
 * Reverses the order of columns that have a name property while maintaining the position
 * of unnamed columns in the series data. Returns original data if operation cannot be completed.
 * @param data The chart data series to process
 * @returns A new ChartDataSeries with named columns reversed, or original data if operation fails
 */
export function reverseNamedColumns(data: ChartDataSeries): ChartDataSeries {
  try {
    // Input validation
    if (!Array.isArray(data)) {
      console.info('data is not an array');
      return data;
    }

    // Create deep clone of original data
    const clonedData = cloneDeep(data);

    // Find indices of columns with 'name' property
    const namedColumnIndices = clonedData.reduce<number[]>((acc, item, index) => {
      if (item?.name) {
        acc.push(index);
      }
      return acc;
    }, []);

    // If no named columns found, return original data
    if (namedColumnIndices.length === 0) {
      console.info('no named columns found');
      return data;
    }

    // Create new array with same length as original
    const result: ChartDataSeries = new Array(clonedData.length);

    // Copy non-named columns to their original positions
    clonedData.forEach((item, index) => {
      if (!item?.name) {
        result[index] = item;
      }
    });

    // Extract, reverse, and place named columns
    const namedColumns = namedColumnIndices.map((index) => clonedData[index]);
    const reversedNamedColumns = namedColumns.reverse();

    namedColumnIndices.forEach((originalIndex, i) => {
      result[originalIndex] = reversedNamedColumns[i];
    });

    // Final validation - if anything went wrong, return original
    if (result.length !== data.length || result.some((item) => item === undefined)) {
      return data;
    }

    return result;
  } catch (error) {
    // If any error occurs, return the original data
    if (error instanceof Error) {
      console.info('error occurred ', error.message);
    }
    return data;
  }
}
